/**
 * DeviceEditor — inline editor for a single MatrixDevice. Rendered
 * directly under the device legend in MatrixViewer when edit mode is
 * on and a device chip is selected.
 *
 * Single-edit-at-a-time: clicking another chip swaps which device this
 * panel shows. Inline (not modal) so the user can see the legend +
 * editor at once and compare colours against the grid behind.
 *
 * Edits commit on input (debounced for the text fields would be over-
 * engineering for a 30-device library — store mutation is cheap).
 */

import { createMemo, For, Show } from 'solid-js';
import type { MatrixDevice, MatrixDeviceColor } from '@opbuttons/schema';
import {
  updateDevice,
  claimsForDevice,
  MATRIX_DEVICE_PALETTE,
} from '../matrix/store.ts';

interface Props {
  device: MatrixDevice;
  onClose: () => void;
  onDelete: () => void;
}

export function DeviceEditor(props: Props) {
  const claimCount = createMemo(() => claimsForDevice(props.device.id));

  return (
    <div class="device-editor" data-hue={props.device.color}>
      <header class="device-editor-head">
        <span class="device-editor-name">Editing {props.device.name}</span>
        <button
          type="button"
          class="device-editor-close"
          onClick={props.onClose}
          title="Close editor (keeps device)"
          aria-label="Close editor"
        >
          ×
        </button>
      </header>

      <div class="device-editor-row">
        <label class="device-editor-field">
          <span class="device-editor-label">Name</span>
          <input
            type="text"
            class="device-editor-input"
            value={props.device.name}
            onInput={(e) => updateDevice(props.device.id, { name: e.currentTarget.value })}
            placeholder="e.g. APC40 mk2 #1"
            maxlength={64}
          />
        </label>

        <div class="device-editor-field">
          <span class="device-editor-label">Color</span>
          <div class="device-editor-swatches" role="radiogroup" aria-label="Device color">
            <For each={MATRIX_DEVICE_PALETTE}>
              {(c: MatrixDeviceColor) => (
                <button
                  type="button"
                  class="device-editor-swatch"
                  data-hue={c}
                  data-selected={props.device.color === c ? 'true' : 'false'}
                  onClick={() => updateDevice(props.device.id, { color: c })}
                  title={c}
                  aria-label={`Set color to ${c}`}
                  role="radio"
                  aria-checked={props.device.color === c}
                />
              )}
            </For>
          </div>
        </div>
      </div>

      <label class="device-editor-field">
        <span class="device-editor-label">Notes (optional)</span>
        <textarea
          class="device-editor-textarea"
          value={props.device.notes ?? ''}
          onInput={(e) => updateDevice(props.device.id, { notes: e.currentTarget.value })}
          placeholder="Model, firmware, location in rack, anything that helps you remember context."
          rows={2}
        />
      </label>

      <footer class="device-editor-foot">
        <span class="device-editor-claim-count">
          <Show when={claimCount() > 0} fallback={'No claims on the matrix.'}>
            {claimCount()} claim{claimCount() === 1 ? '' : 's'} on the matrix.
          </Show>
        </span>
        <button
          type="button"
          class="device-editor-delete"
          onClick={props.onDelete}
        >
          Delete device
        </button>
      </footer>
    </div>
  );
}
